import { Box, Collapse, IconButton, Stack, styled } from '@mui/material';
import { useMemo, useState } from 'react';
import { DynamicIcon } from 'src/components';
import { useActivityChildren } from 'src/graphql/entities/activity-feed/hooks/use-activity-children';
import { ActivityFeed } from 'src/graphql/entities/activity-feed/hooks/use-activity-feed';
import { sortByDate } from 'src/utils/sorting';
import { ActivityRow } from './activity-row';
import { StyledTypography } from './styled/typography.styled';

/*
 * The run's geometry, in theme spacing units, all of it derived from one gutter (see the prototype's
 * `.run-head` / `.run-body`). The gutter holds the chevron; the rail sits at its centre and a nested
 * row starts one step further in than the run's own icon.
 */
/** Inset before the chevron, so it is not flush against the timeline edge. */
const TOGGLE_INSET = 1;
/** The chevron's box. Ends the gutter, after which `ChatActivityContainer` places the run's icon. */
const TOGGLE_SIZE = 2;
/** Puts the rail under the centre of the chevron whose children it groups. */
const RAIL_INSET = TOGGLE_INSET + TOGGLE_SIZE / 2;
/** Step from the rail to a nested row, landing it in from the run's icon above. */
const RAIL_TO_ROW = 2;
/** The top margin `ChatActivityContainer` puts on every row, mirrored to line the chevron up. */
const ROW_TOP_MARGIN = 1;

const RunChevron = styled(DynamicIcon)(({ theme }) => ({
  fontSize: '1rem',
  color: theme.palette.custom.tertiary.tertiary40,
}));

const RunToggle = styled(IconButton)(({ theme }) => ({
  padding: 0,
  marginLeft: theme.spacing(TOGGLE_INSET),
  // The row this sits beside carries its own top margin, inside the neighbouring flex item, so
  // without matching it here centring lands the chevron half that margin above the row's text.
  marginTop: theme.spacing(ROW_TOP_MARGIN),
  width: theme.spacing(TOGGLE_SIZE),
  height: theme.spacing(TOGGLE_SIZE),
  flexShrink: 0,
}));

/**
 * The "caused by" rail. Deliberately the same colour as the feed's horizontal day divider
 * (`MessageGroupSeparatorLine`): the rail groups rows, it is not an accent, so it should recede.
 */
const RunBody = styled(Stack)(({ theme }) => ({
  marginLeft: theme.spacing(RAIL_INSET),
  paddingLeft: theme.spacing(RAIL_TO_ROW),
  paddingBottom: theme.spacing(0.5),
  borderLeft: `1px solid ${theme.palette.custom.OV.sys.outlineVariant}`,
}));

/**
 * Whether an activity is offered as an expandable run.
 *
 * A `Macro`/`execute` or `Behavior`/`execute` activity is a run: it is the one kind of activity that
 * can have caused others, which is the same special case the feed's own root filter makes.
 * Everything else is a leaf, so it gets no toggle and never costs a request to discover that it has
 * nothing inside.
 *
 * The feed does not report whether a given run actually caused anything — the children are not
 * fetched with it, because selecting them made the feed query pay for a join it could not afford.
 * So the entity and operation alone decide this, and a run may open onto an empty body.
 *
 * `actorUid` is required throughout: without it there is no key to load the children with.
 */
const isExpandableActivity = (activity: ActivityFeed) =>
  !!activity.actorUid && activity.activityType === 'execute';

function getStatusMessage(input: {
  loading: boolean;
  hasError: boolean;
  isEmpty: boolean;
}): string | null {
  if (input.loading) {
    return 'Loading nested activities…';
  }
  if (input.hasError) {
    return 'Could not load nested activities.';
  }
  // Reachable for any run: whether it caused anything that renders is only known once its children
  // have been fetched.
  return input.isEmpty ? 'No nested activities.' : null;
}

/**
 * Mounted only while its run is open, which is what makes the nested feed lazy: mounting is the only
 * gate on the request, so a conversation full of runs costs nothing until one is expanded.
 */
const ActivityRunChildren = ({ activity }: { activity: ActivityFeed }) => {
  const { records, loading, error } = useActivityChildren(
    // Only reached once `isExpandableActivity` has established there is an `actorUid`.
    activity.actorUid ?? '',
    activity.partnerId,
  );

  const children = useMemo(
    () => [...(records ?? [])].sort(sortByDate('timestamp')),
    [records],
  );

  const message = getStatusMessage({
    loading,
    hasError: !!error,
    isEmpty: children.length === 0,
  });

  return (
    <>
      {message ? <StyledTypography>{message}</StyledTypography> : null}
      {children.map((child) => (
        <Box key={child.id}>
          {/*
           * A run renders nodes and a node may be a run, so this is the one unavoidable forward
           * reference — the recursion is the point, and keeping it inside this module is what
           * avoids a callback prop or an import cycle with `activity-row.tsx`.
           */}
          {/* eslint-disable-next-line @typescript-eslint/no-use-before-define */}
          <ActivityNode activity={child} />
        </Box>
      ))}
    </>
  );
};

/**
 * A collapsible group of the activities one activity caused.
 *
 * Opening a run fetches its children, one request per run the user actually opens; Apollo caches the
 * result, so collapsing and re-expanding is free. Each of those children may in turn be a run, so a
 * run nests as deep as the causality chain goes — again at most one request per level opened.
 */
const ActivityRun = ({ activity }: { activity: ActivityFeed }) => {
  const [open, setOpen] = useState(false);
  return (
    <Stack>
      <Stack direction="row" alignItems="center">
        <RunToggle
          size="small"
          aria-expanded={open}
          aria-label={open ? 'Collapse nested activities' : 'Expand nested activities'}
          onClick={() => setOpen((it) => !it)}
        >
          <RunChevron name={open ? 'expand_more' : 'chevron_right'} />
        </RunToggle>
        <ActivityRow activity={activity} />
      </Stack>
      {/* `unmountOnExit` keeps the children query from being mounted at all until first expand. */}
      <Collapse in={open} unmountOnExit>
        <RunBody>
          <ActivityRunChildren activity={activity} />
        </RunBody>
      </Collapse>
    </Stack>
  );
};

/**
 * One activity, as a plain row or as an expandable run depending on whether it could have caused
 * others.
 *
 * The recursion lives in this module so a run can render nested nodes by calling this directly,
 * with no callback prop and no import cycle: `ActivityRow` is a leaf that never reaches back here.
 */
export const ActivityNode = ({ activity }: { activity: ActivityFeed }) =>
  isExpandableActivity(activity) ? (
    <ActivityRun activity={activity} />
  ) : (
    <ActivityRow activity={activity} />
  );
